import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { verifyCohortAccess } from "../../features/cohort/cohortSlice";

const CAMP_APEX_LOGO = "/images/code-cohort/camp-apex-logo.png";
const CODE_WITH_SALLY_LOGO = "/images/code-cohort/code-with-sally-logo.png";

const WEEKS_DATA = [
  { week: 1, title: "Apex Fundamentals (Variables, Logic, Loops, Collections)" },
  { week: 2, title: "Collections, Object-Oriented Programming & sObjects" },
  { week: 3, title: "SOQL, SOSL, Triggers, Unit Testing & Exception Handling" },
  { week: 4, title: "" },
  { week: 5, title: "" },
  { week: 6, title: "" },
];

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700&family=Outfit:wght@300;400;500;600;700&display=swap');

  .font-display { font-family: 'Outfit', sans-serif; }
  .font-mono-custom { font-family: 'JetBrains Mono', monospace; }

  .gradient-border {
    background: linear-gradient(135deg, #0a0a0f 0%, #0a0a0f 100%);
    position: relative;
  }
  .gradient-border::before {
    content: '';
    position: absolute;
    inset: 0;
    padding: 1px;
    background: linear-gradient(135deg, #f59e0b 0%, #10b981 50%, #3b82f6 100%);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    border-radius: inherit;
    pointer-events: none;
  }

  .hero-glow {
    background: radial-gradient(ellipse 80% 50% at 50% -20%, rgba(245, 158, 11, 0.15), transparent);
  }

  .card-hover {
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  .card-hover:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(245, 158, 11, 0.1);
  }
`;

function LogoWithFallback({ src, alt, fallbackText, gradientFrom, gradientTo }) {
  const [imageError, setImageError] = React.useState(false);

  if (imageError) {
    return (
      <div
        className="w-14 h-14 rounded-xl flex items-center justify-center"
        style={{ background: `linear-gradient(to bottom right, ${gradientFrom}, ${gradientTo})` }}
      >
        <span className="font-display font-bold text-2xl text-white">{fallbackText}</span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className="w-14 h-14 rounded-xl object-contain"
      onError={() => setImageError(true)}
    />
  );
}

function CodeCohortLanding() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { hasAccess, isLoading } = useSelector((state) => state.cohort);

  useEffect(() => {
    if (user?.[0]?.email) {
      dispatch(verifyCohortAccess());
    }
  }, [dispatch, user]);

  const handleEnterClick = (e) => {
    if (!user?.[0]?.email) {
      e.preventDefault();
      navigate("/auth", { state: { returnTo: "/6-week-challenge/learn" } });
    }
  };

  const isLoggedIn = user?.[0]?.email;
  const showEnterButton = isLoggedIn && hasAccess;
  const showLoginPrompt = !isLoggedIn;

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-zinc-100">
      <style>{styles}</style>

      <div className="hero-glow">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-16">
          {/* Dual Logo Header */}
          <div className="flex items-center justify-center gap-8 mb-12">
            <div className="flex items-center gap-3">
              <LogoWithFallback
                src={CAMP_APEX_LOGO}
                alt="Camp Apex"
                fallbackText="CA"
                gradientFrom="#f59e0b"
                gradientTo="#ea580c"
              />
              <span className="font-display font-semibold text-xl text-zinc-200">Camp Apex</span>
            </div>

            <div className="text-zinc-600 text-2xl font-light">×</div>

            <div className="flex items-center gap-3">
              <LogoWithFallback
                src={CODE_WITH_SALLY_LOGO}
                alt="Code with Sally"
                fallbackText="CS"
                gradientFrom="#10b981"
                gradientTo="#14b8a6"
              />
              <span className="font-display font-semibold text-xl text-zinc-200">Code with Sally</span>
            </div>
          </div>

          {/* CTA Banner */}
          <div className="gradient-border rounded-2xl p-8 text-center mb-16">
            <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-4">
              The 6-Week Challenge
            </h1>
            <p className="font-display text-2xl text-white font-semibold mb-2">
              Applications close February 1st
            </p>
            <p className="font-mono-custom text-sm text-yellow-400 mb-6">
              Only 20 spots available
            </p>
            <a
              href="https://tally.so/forms/Pd1Yp1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-gradient-to-r from-amber-500 to-orange-500 text-black font-display font-semibold px-8 py-4 rounded-xl hover:from-amber-400 hover:to-orange-400 transition-all"
            >
              Apply Now
            </a>
          </div>

          {/* The Collaboration Narrative */}
          <section className="mb-20">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8">
                <div className="flex items-center gap-4 mb-6">
                  <LogoWithFallback
                    src={CAMP_APEX_LOGO}
                    alt="Camp Apex"
                    fallbackText="CA"
                    gradientFrom="#f59e0b"
                    gradientTo="#ea580c"
                  />
                  <h3 className="font-display font-semibold text-xl text-amber-400">Camp Apex</h3>
                </div>
                <p className="font-display text-zinc-400 leading-relaxed">
                  A platform dedicated to teaching Salesforce Apex programming through
                  hands-on challenges and real-world projects. We believe in learning by doing.
                </p>
              </div>

              <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8">
                <div className="flex items-center gap-4 mb-6">
                  <LogoWithFallback
                    src={CODE_WITH_SALLY_LOGO}
                    alt="Code with Sally"
                    fallbackText="CS"
                    gradientFrom="#10b981"
                    gradientTo="#14b8a6"
                  />
                  <h3 className="font-display font-semibold text-xl text-emerald-400">Code with Sally</h3>
                </div>
                <p className="font-display text-zinc-400 leading-relaxed">
                  A community-driven educational initiative focused on teaching Salesforce development through clear explanations, live sessions, and YouTube content, using real-world examples. It offers free, open learning designed to help developers—especially beginners—build confidence across Apex, LWC, and other Salesforce development concepts by breaking down complexity into practical, easy-to-follow lessons.
                </p>
              </div>
            </div>

            <div className="mt-8 bg-gradient-to-r from-amber-500/10 via-emerald-500/10 to-amber-500/10 border border-zinc-800 rounded-2xl px-3 py-6">
              <div className="font-display text-lg text-zinc-300 leading-relaxed px-8">
                <p className="mb-4">
                  Together, we're launching a <span className="text-amber-400 font-semibold">6-week guided learning </span> experience to introduce Salesforce Apex fundamentals. Each week combines curated Code With Sally videos with hands-on Camp Apex challenges, supported by live office hours and a strong learning community.
                </p>
                <p className="mb-4">
                  Learners follow a focused weekly plan - watching selected Code With Sally lessons and completing corresponding Camp Apex challenges before moving on to the next week.
                </p>
                <p className="mb-3">The program includes:</p>
                <ul className="list-disc list-inside space-y-2">
                  <li>A live kickoff session to welcome learners and explain the journey</li>
                  <li>Weekly office hours for questions, guidance, and support</li>
                  <li>A final celebration session to reflect on progress and achievements</li>
                  <li>Outstanding participants may receive exclusive SWAG and could qualify for a Salesforce certification exam voucher</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Roadmap Section */}
          <section>
            <h2 className="font-display text-2xl font-semibold text-center text-zinc-200 mb-10">
              Program Roadmap
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {WEEKS_DATA.map((week) => (
                <div
                  key={week.week}
                  className="rounded-xl p-5 border bg-zinc-900/80 border-zinc-700"
                >
                  <div className="font-mono-custom text-xs uppercase tracking-wider mb-2 text-amber-400">
                    Week {week.week}
                  </div>
                  <h3 className="font-display font-medium text-zinc-200 text-sm leading-tight">
                    {week.title || "Coming soon"}
                  </h3>
                </div>
              ))}
            </div>
          </section>

          {/* Enter Learning Environment CTA */}
          <div className="mt-16 text-center">
            {isLoading ? (
              <div className="inline-flex items-center gap-3 bg-zinc-900 border border-zinc-700 text-zinc-400 font-display font-medium px-8 py-4 rounded-xl">
                <span>Checking access...</span>
              </div>
            ) : showEnterButton ? (
              <Link
                to="/6-week-challenge/learn"
                className="inline-flex items-center gap-3 bg-zinc-900 border border-zinc-700 hover:border-amber-500/50 text-zinc-200 font-display font-medium px-8 py-4 rounded-xl transition-all"
              >
                <span>Enter Learning Environment</span>
                <span className="text-amber-400">→</span>
              </Link>
            ) : <></>
            }
          </div>
        </div>
      </div>
    </div>
  );
}

export default CodeCohortLanding;
